/**
 * Creates props for template compliance graph to be used with BlankCard component
 * @param {Array} data - Array of daily data objects containing template compliance information
 * @param {Object} options - Additional options like height, chartHeight, etc.
 * @returns {Object} - Props object ready to spread into BlankCard component
 */
export const createTemplateCompliance = (data, options = {}) => {

  // Set default values for options
  const {
    height = 300,
    chartHeight = 250,
    thresholds = { low: 70, medium: 80, high: 80 },
    selectedDays = [],
    templateDates = []
  } = options;

  // Calculate the average compliance for the badge display
  const validData = data.filter(item => 
    item && item.compliance !== undefined && item.compliance !== null
  );
  
  const averageCompliance = validData.length > 0
    ? (validData.reduce((sum, item) => sum + item.compliance, 0) / validData.length).toFixed(1)
    : "0";
  
   // Determine status based on compliance average
   let status = "neutral";
   let badge = `${averageCompliance}%`;
   
   if (validData.length === 0) {
     status = "neutral";
     badge = "No Data";
   } else if (averageCompliance >= thresholds.high) {
     status = "great"; // Changed from "good" to "great" for consistency
     badge = `Fully Compliant!`;
   } else if (averageCompliance >= thresholds.low) {
     status = "good"; // Changed from "warning" to "good"
     badge = `Almost There`;
   } else {
     status = "critical";
     badge = `Needs Attention`;
   }

  // Generate a more informative description
  const generateDescription = () => {
    const complianceNum = parseFloat(averageCompliance);
    
    // Base description
    let description = `Average compliance rate: <strong>${averageCompliance}%</strong>`;
    
    // Add comparison to goal thresholds
    if (complianceNum >= thresholds.high) {
      description += `, which meets or exceeds the goal of <strong>${thresholds.high}%</strong>`;
    } else {
      const deficit = (thresholds.high - complianceNum).toFixed(1);
      description += `, which is <strong>${deficit}%</strong> below the goal of <strong>${thresholds.high}%</strong>`;
    }
    
    return description;
  };

  // Define color ranges based on compliance thresholds
  const colorRanges = [
    {
      from: 0,
      to: 0,
      color: "#e5e5e5" // Gray for missing data
    },
    {
      from: 0.1,
      to: thresholds.low,
      color: "#f87171" // Low compliance (red)
    },
    {
      from: thresholds.low + 0.01,
      to: thresholds.high - 0.01,
      color: "#FFCC80" // Medium compliance (yellow/orange)
    },
    {
      from: thresholds.high,
      to: 100,
      color: "#4ade80" // High compliance (green)
    }
  ];

  function createToolTipText(templateDates) {
    const sortedDates = [...templateDates].sort((a, b) => new Date(a) - new Date(b));

    return (
        <ul className="list-disc pl-4">
            {sortedDates.map((date, index) => (
                <li key={index}>
                    {index === sortedDates.length - 1
                        ? `From ${date} onwards uses the newest template`
                        : `From ${date} to ${sortedDates[index + 1]} uses version ${index + 1}`}
                </li>
            ))}
        </ul>
    );
}

   // Add tooltip text
  const tooltipText = createToolTipText(templateDates);

  // Return props for BlankCard with heatmap configuration
  return {
    title: "Template Compliance",
    description: generateDescription(),
    tooltip: tooltipText,
    badge: badge,
    chartType: "heatmap",
    chartData: data, // Pass the raw data, BlankCard will transform it
    chartHeight,
    height,
    status,
    stats: {},
    legend: [
      { label: `Compliant (${thresholds.high}%+)`, color: "#4ade80" },
      { label: `Almost there (${thresholds.low}%-${thresholds.high}%)`, color: "#FFCC80" },
      { label: `Not Compliant (0-${thresholds.low}%)`, color: "#f87171" },
      { label: "No Data", color: "#e5e5e5" }
    ],
    options: {
      colorRanges,
      thresholds,
      selectedDays
    }
  };
};

export default createTemplateCompliance;
